/**
 * Iter T · Phase 1 — Admin Quote Activity detail.
 *
 * Read-only view of a single Quote Activity. Shows General info,
 * the exact Customer Input (verbatim, no reformatting), and the
 * exact System Interpretation. The only admin action is "Mark as read".
 * Cross-links to the linked order when the quote has converted.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Loader2, ArrowLeft, CheckCircle2, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { detailLong } from "@/lib/businessTime";
import { mergeMarkReadResponse } from "@/lib/quoteDetailHydration";
import { StatusPill } from "@/components/admin/QuoteList";
import ConvertQuoteModal from "@/components/admin/ConvertQuoteModal";
import QuoteAdjustmentsPanel from "@/components/admin/QuoteAdjustmentsPanel";
import EmailFunnelPanel from "@/components/admin/EmailFunnelPanel";
import VariantBadge from "@/components/admin/VariantBadge";

// Phase Q4 — canonical reason → admin-friendly label. Kept in sync
// with backend `FEEDBACK_REASONS` in `routers/quotes_admin.py`.
const FEEDBACK_LABELS = {
  price_too_high:   "The price was higher than expected",
  just_checking:    "Just checking the price",
  found_elsewhere:  "Already found the stickers somewhere else",
  no_longer_needed: "No longer needs stickers",
  ordering_later:   "Planning to order later",
  shipping_cost:    "Shipping cost",
  payment_method:   "Payment method",
  have_question:    "Has a question before ordering",
  quote_problem:    "Something is wrong with quote",
  other:            "Other",
};


function Field({ label, value, mono = false, testid }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 py-1.5 text-sm border-b border-neutral-100 last:border-b-0">
      <dt className="text-[var(--smoke)] uppercase tracking-wider text-[10px] font-bold pt-1">{label}</dt>
      <dd className={mono ? "font-mono break-all" : ""} data-testid={testid}>
        {value ?? <span className="italic opacity-60">—</span>}
      </dd>
    </div>
  );
}


function Section({ title, children, testid }) {
  return (
    <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink)] mb-3">
        {title}
      </div>
      {children}
    </div>
  );
}


export default function QuoteDetail({ publicId, onBack }) {
  const [q, setQ] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showConvert, setShowConvert] = useState(false);

  const load = useCallback(async () => {
    try {
      setErr(null);
      const r = await api.get(`/admin/quotes/${encodeURIComponent(publicId)}`);
      setQ(r.data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [publicId]);

  useEffect(() => { load(); }, [load]);

  // Auto-mark-read the first time an admin opens the detail — matches
  // the spec "once viewed by the administrator, it can become 'read.'"
  // Idempotent: repeated opens are no-ops after the first.
  //
  // FIRST-OPEN HYDRATION FIX (2026-02-22, Msg 241) — the backend
  // `/mark-read` endpoint returns the RAW `quote_activities` document
  // (no `price_breakdown`, no `finalization`, no `linked_order`,
  // no `update_email_status`). Previously we did `setQ(r.data)` which
  // overwrote the enriched detail with the raw doc, so the editable
  // Price Breakdown + custom-items controls vanished on first open
  // (they only reappeared after close/reopen because on the second
  // open `admin_read === true` and this effect short-circuited).
  // Fix: merge back only the small lifecycle fields the mark-read
  // response is authoritative for — never replace the whole quote.
  useEffect(() => {
    if (!q || q.admin_read) return;
    let cancelled = false;
    api.post(`/admin/quotes/${encodeURIComponent(publicId)}/mark-read`)
      .then((r) => {
        if (cancelled || !r.data) return;
        setQ((prev) => mergeMarkReadResponse(prev, r.data));
      })
      .catch(() => { /* stay in the current state — read flag is best-effort */ });
    return () => { cancelled = true; };
  }, [q, publicId]);

  const markUnreadUnavailable = () => {
    toast("Reopening a read quote is not supported in Phase 1.");
  };

  if (err) {
    return (
      <div>
        <button onClick={onBack} className="btn-ghost text-sm mb-4" data-testid="quote-detail-back">
          <ArrowLeft size={14} /> Back to Quotes
        </button>
        <div className="border-2 border-red-800 bg-red-50 text-red-900 rounded-lg px-3 py-2 text-sm">
          {err}
        </div>
      </div>
    );
  }

  if (!q) {
    return (
      <div className="p-10 text-center text-[var(--smoke)]">
        <Loader2 className="animate-spin inline mr-2" size={16} /> Loading quote…
      </div>
    );
  }

  const totals = q.quote_snapshot?.totals || {};
  const lines = q.quote_snapshot?.lines || [];
  const parsed = q.parsed_snapshot || {};
  const unknowns = parsed.unknowns || [];
  const duplicates = parsed.duplicates || [];
  const invalids = parsed.invalids || [];
  const items = parsed.items || [];

  return (
    <div data-testid="quote-detail">
      <button onClick={onBack} className="btn-ghost text-sm mb-4" data-testid="quote-detail-back">
        <ArrowLeft size={14} /> Back to Quotes
      </button>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="kicker kicker-cream">Quote Activity</div>
          <h2 className="font-display text-3xl md:text-4xl mt-1 font-mono">{q.public_id}</h2>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusPill status={q.status} admin_read={q.admin_read} />
            {/* P6 · Missing-Sticker variant badge — hoisted to the top
                so it is always visible next to Quote ID + status, not
                buried inside PriceBreakdown. WHITE renders at the same
                visual weight as BLUE / GOLD CRUMPLE (never a pale pill). */}
            <VariantBadge
              variant={q.variant
                || q.pricing_snapshot?.variant
                || q.quote_snapshot?.variant
                || null}
              size="lg"
              testid="quote-detail-variant-badge"
            />
            {q.converted_order_public_id && (
              <Link
                to={`/admin/fulfillment/${q.converted_order_public_id}`}
                className="ml-1 inline-flex items-center gap-1 border-2 border-[var(--ink)] rounded-full px-2.5 py-0.5 text-[11px] font-bold bg-emerald-100 text-emerald-900 hover:bg-emerald-200 no-underline"
                data-testid="quote-detail-order-link"
              >
                <CheckCircle2 size={11} /> Order {q.converted_order_public_id}
              </Link>
            )}
          </div>
        </div>
        {q.status !== "converted" && !q.converted_order_public_id && (
          <button
            onClick={() => setShowConvert(true)}
            className="btn-red text-sm inline-flex items-center gap-1"
            data-testid="quote-detail-convert-btn"
          >
            <CheckCircle2 size={14} /> Convert to Order
          </button>
        )}
      </div>

      {showConvert && (
        <ConvertQuoteModal
          quote={q}
          onClose={() => setShowConvert(false)}
          onConverted={() => load()}
        />
      )}

      {/* Q5.4 · Compact lifecycle timeline. Reads only fields the
          admin detail endpoint already returns; no extra network. */}
      <QuoteJourney q={q} />

      {/* Q5.3 · Manual sales actions strip. Compact, sits directly
          above the info sections so admin sees state + primary CTAs
          before scrolling. */}
      <SalesActions q={q} onChanged={load} />

      {/* Phase 2 · Admin Quote Adjustments panel — discount + custom
          items + finalize/reopen/convert lifecycle. */}
      <QuoteAdjustmentsPanel quote={q} onChanged={load} />

      {/* Phase 3 · Email / Recovery Funnel — sent/delivered/opened/
          clicked per stage + resume + conversion classification. */}
      <EmailFunnelPanel publicId={q.public_id} />

      <div className="grid gap-5">
        <Section title="General" testid="quote-detail-general">
          <dl>
            <Field label="Quote ID" value={q.public_id} mono testid="qd-public-id" />
            <Field label="Created" value={detailLong(q.created_at)} testid="qd-created" />
            <Field label="Last activity" value={detailLong(q.last_activity_at)} testid="qd-last-activity" />
            <Field label="Source" value={q.quote_source} mono />
            <Field label="Outcome" value={q.outcome} mono testid="qd-outcome" />
            <Field label="Version" value={q.quote_version} mono />
            <Field label="Album" value={q.album_slug} mono />
            <Field label="Pricing list" value={q.pricing_list_id} mono />
            <Field label="Pricing version" value={q.pricing_version} mono />
            <Field label="Quote hash" value={q.quote_hash} mono testid="qd-hash" />
          </dl>
        </Section>

        <Section title="Customer" testid="quote-detail-customer">
          <dl>
            {q.customer?.is_anonymous ? (
              <>
                <Field label="Type" value={<span className="italic">Anonymous</span>} testid="qd-customer-type" />
                <Field label="Session ID" value={q.session_id} mono testid="qd-session-id" />
              </>
            ) : (
              <>
                <Field label="First name" value={q.customer?.first_name} testid="qd-customer-first-name" />
                <Field label="Last name" value={q.customer?.last_name} testid="qd-customer-last-name" />
                <Field label="Name" value={q.customer?.name} testid="qd-customer-name" />
                <Field label="Email" value={q.customer?.email} mono testid="qd-customer-email" />
                <Field label="Phone" value={q.customer?.phone} mono testid="qd-customer-phone" />
                <Field label="Collector ID" value={q.customer?.collector_id} mono />
                <Field
                  label="Newsletter"
                  value={q.newsletter_opt_in
                    ? <span className="inline-flex items-center gap-1 text-emerald-900 font-semibold" data-testid="qd-newsletter-yes">
                        <CheckCircle2 size={12} /> Opted in{q.newsletter_opt_in_at ? ` · ${detailLong(q.newsletter_opt_in_at)}` : ""}
                      </span>
                    : <span className="italic opacity-70" data-testid="qd-newsletter-no">Not opted in</span>}
                />
                <Field label="Session ID" value={q.session_id} mono testid="qd-session-id" />
              </>
            )}
          </dl>
        </Section>

        {/* Phase Q4 — feedback + recovery status. Rendered only when the
            customer has actually submitted feedback so historical rows
            stay uncluttered. Recovery status is shown either way. */}
        <Section title="Feedback & Recovery" testid="quote-detail-feedback">
          <dl>
            {q.feedback?.reason ? (
              <>
                <Field
                  label="Feedback reason"
                  value={
                    <span data-testid="qd-feedback-reason">
                      {FEEDBACK_LABELS[q.feedback.reason] || q.feedback.reason}
                    </span>
                  }
                />
                <Field
                  label="Feedback comment"
                  value={q.feedback.comment
                    ? <span className="whitespace-pre-wrap" data-testid="qd-feedback-comment">{q.feedback.comment}</span>
                    : <span className="italic opacity-70">—</span>}
                />
                <Field
                  label="Submitted at"
                  value={q.feedback.submitted_at ? detailLong(q.feedback.submitted_at) : null}
                  testid="qd-feedback-submitted-at"
                />
              </>
            ) : (
              <Field
                label="Feedback"
                value={<span className="italic opacity-70" data-testid="qd-feedback-none">No feedback submitted</span>}
              />
            )}
            <Field
              label="Recovery"
              value={(q.recovery_state?.code || q.quote_recovery_suppressed) ? (
                <span
                  className={
                    q.recovery_state?.code === "eligible_due_now" ? "inline-flex items-center gap-1 text-emerald-900 font-semibold" :
                    q.recovery_state?.code === "eligible_scheduled" ? "inline-flex items-center gap-1 text-emerald-800 font-semibold" :
                    q.recovery_state?.code === "superseded_by_newer_quote" ? "inline-flex items-center gap-1 text-neutral-700 font-semibold" :
                    q.recovery_state?.code === "expired_past_max_age" ? "inline-flex items-center gap-1 text-neutral-700 font-semibold" :
                    q.recovery_state?.code === "customer_cooldown" ? "inline-flex items-center gap-1 text-amber-800 font-semibold" :
                    q.recovery_state?.code === "converted_this_quote" || q.recovery_state?.code === "order_in_progress_this_quote" ? "inline-flex items-center gap-1 text-emerald-900 font-semibold" :
                    q.recovery_state?.code === "customer_has_conversion" ? "inline-flex items-center gap-1 text-emerald-800 font-semibold" :
                    "inline-flex items-center gap-1 text-[var(--panini-red)] font-semibold"
                  }
                  data-testid={`qd-recovery-state-${q.recovery_state?.code || 'suppressed'}`}
                  title={q.recovery_state?.next_eligible_at ? `next: ${q.recovery_state.next_eligible_at}` : ""}
                >
                  <CheckCircle2 size={12} />
                  {q.recovery_state?.label || (q.quote_recovery_suppressed
                    ? `Suppressed — ${FEEDBACK_LABELS[q.quote_recovery_suppressed_reason] || q.quote_recovery_suppressed_reason || "opted out"}`
                    : "Eligible for follow-up")}
                  {q.recovery_state?.code === "superseded_by_newer_quote" && q.recovery_state?.detail?.newest_quote_public_id && (
                    <span className="text-[10px] font-mono opacity-70 ml-1">
                      → {q.recovery_state.detail.newest_quote_public_id}
                    </span>
                  )}
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 text-emerald-900 font-semibold"
                  data-testid="qd-recovery-eligible"
                >
                  <CheckCircle2 size={12} /> Eligible for follow-up
                </span>
              )}
            />
          </dl>
        </Section>

        {/* Customer Input — VERBATIM. Never normalise, never reformat.
            Rendered inside a <pre> with whitespace-pre-wrap so line
            breaks + spacing survive exactly as pasted. */}
        <Section title="Customer Input (verbatim)" testid="quote-detail-raw">
          <pre
            className="whitespace-pre-wrap break-words text-sm bg-neutral-50 border border-neutral-300 rounded-lg p-3 max-h-72 overflow-auto"
            data-testid="qd-raw-text"
          >
            {q.raw_text || ""}
          </pre>
        </Section>

        <Section title="System Interpretation" testid="quote-detail-interpretation">
          {/* Parser results */}
          <div className="grid md:grid-cols-3 gap-3 mb-4">
            <StatBox label="Total stickers" value={totals.sticker_count ?? items.reduce((s, i) => s + (i.qty || 0), 0)} />
            <StatBox label="Sellable" value={lines.reduce((s, l) => s + (l.qty || 0), 0)} />
            <StatBox label="Unknowns / invalid" value={unknowns.length + invalids.length} />
          </div>

          {!!items.length && (
            <details className="mb-3" open>
              <summary className="text-xs font-bold uppercase tracking-widest cursor-pointer text-[var(--ink)]">
                Parsed sticker list ({items.length})
              </summary>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {items.map((it, i) => {
                  const label = it.display_name || it.subject || it.player_name;
                  return (
                    <span key={i} className="inline-flex items-center gap-1 border border-neutral-400 rounded px-1.5 py-0.5 text-xs font-mono" data-testid={`quote-item-${it.code}`}>
                      {it.code}
                      {label && <span className="font-sans opacity-80"> · {label}</span>}
                      <span className="opacity-60">×{it.qty}</span>
                    </span>
                  );
                })}
              </div>
            </details>
          )}

          {!!duplicates.length && (
            <details className="mb-3">
              <summary className="text-xs font-bold uppercase tracking-widest cursor-pointer text-amber-800">
                Duplicates ({duplicates.length})
              </summary>
              <pre className="mt-2 text-xs font-mono bg-amber-50 border border-amber-300 rounded p-2 overflow-auto max-h-40">
                {JSON.stringify(duplicates, null, 2)}
              </pre>
            </details>
          )}

          {!!unknowns.length && (
            <details className="mb-3">
              <summary className="text-xs font-bold uppercase tracking-widest cursor-pointer text-red-800">
                Unknown entries ({unknowns.length})
              </summary>
              <pre className="mt-2 text-xs font-mono bg-red-50 border border-red-300 rounded p-2 overflow-auto max-h-40">
                {JSON.stringify(unknowns, null, 2)}
              </pre>
            </details>
          )}

          {!!invalids.length && (
            <details className="mb-3">
              <summary className="text-xs font-bold uppercase tracking-widest cursor-pointer text-red-800">
                Invalid entries ({invalids.length})
              </summary>
              <pre className="mt-2 text-xs font-mono bg-red-50 border border-red-300 rounded p-2 overflow-auto max-h-40">
                {JSON.stringify(invalids, null, 2)}
              </pre>
            </details>
          )}

          <div className="mt-4 border-t border-neutral-200 pt-3">
            <div className="text-xs font-bold uppercase tracking-widest mb-2">Pricing</div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
              {["subtotal", "tax", "shipping", "discount", "total"].map((k) => {
                const raw = totals[k];
                if (raw === undefined || raw === null) return null;
                // P3.2 · Guard against object-shaped totals (engine
                // emits `shipping = {code,label,cost}` and `tax =
                // {rate,amount,jurisdiction}`). Reading `.cost` /
                // `.amount` prevents the historical $NaN render.
                let numeric = null;
                let label = null;
                if (typeof raw === "object") {
                  if (k === "shipping") {
                    const cost = Number(raw.cost);
                    if (Number.isFinite(cost)) numeric = cost;
                    // Local pickup ($0 shipping) is a semantic
                    // choice, not a missing number — render "Free /
                    // Local Pickup" instead of $0.00.
                    if ((raw.code === "local_pickup") && (!(cost > 0))) {
                      label = "Local Pickup";
                    }
                  } else if (k === "tax") {
                    const amount = Number(raw.amount);
                    if (Number.isFinite(amount)) numeric = amount;
                  }
                } else {
                  const n = Number(raw);
                  if (Number.isFinite(n)) numeric = n;
                }
                if (numeric === null && label === null) return null;
                return (
                  <React.Fragment key={k}>
                    <dt className="text-[var(--smoke)] capitalize">{k}</dt>
                    <dd className="font-mono" data-testid={`quote-system-totals-${k}`}>
                      {label ? label : `$${numeric.toFixed(2)}`}
                    </dd>
                  </React.Fragment>
                );
              })}
            </dl>
          </div>
        </Section>

        <PriceBreakdown quote={q} onReload={load} />
      </div>
    </div>
  );
}


function PriceBreakdown({ quote, onReload }) {
  const bd = quote?.price_breakdown;
  if (!bd) return null;
  const editable = !!bd.editable && bd.available !== false && !bd.read_only_reason;
  const persistedSnap = quote?.pricing_snapshot;
  const snapshotUpdatedAt = bd.snapshot_updated_at || null;
  // P3.2 · Canonical variant for this quote. Falls back to `white`
  // for legacy rows and pre-P3.2 fixtures.
  const quoteVariant = quote?.variant
    || quote?.pricing_snapshot?.variant
    || quote?.quote_snapshot?.variant
    || "white";

  // Legacy quote with no frozen line data — render an always-visible
  // read-only Quote Items table using `quote_snapshot.lines`, plus a
  // deliberate "Rebuild editable breakdown using current pricing"
  // action. Owner directive 2026-02-19 (P1): admins must never see
  // just the "not available" stub, and Rebuild must never silently
  // overwrite frozen pricing without explicit Save.
  if (bd.available === false) {
    return (
      <Section title="Quote Items (frozen)" testid="quote-price-breakdown">
        <QuoteItemsReadOnly quote={quote} variant={quoteVariant} />
        <LegacyRebuildPanel quote={quote} onReload={onReload} />
      </Section>
    );
  }
  // P3.1 · Legacy quote with frozen lines but no explicit tax rate:
  // still show the breakdown read-only, but with a clear reason.
  if (bd.read_only_reason === "legacy_financial_snapshot_incomplete") {
    // fall through — table renders but every row's Actions cell shows
    // "Locked" (via `editable=false`). Also surface an explanatory
    // banner right above the table.
  }

  return (
    <Section title="Price Breakdown" testid="quote-price-breakdown">
      {/* P3.2 · Quote-level variant identifier. Prominent badge for
          non-white variants so Admin can never misidentify a colored
          parallel quote as White. White gets a simple monochrome
          identifier to keep the header uncluttered. */}
      <QuoteVariantBadge variant={quoteVariant} placement="admin" />
      {!editable && (
        <div className="mb-3 text-xs text-[var(--smoke)] italic" data-testid="p3-read-only-note">
          {bd.read_only_reason === "legacy_financial_snapshot_incomplete"
            ? "Detailed historical pricing is available, but this quote cannot be edited because the original tax/financial configuration is incomplete."
            : "This quote is finalized — pricing is read-only."}
        </div>
      )}
      {bd.editable_persist_required && (
        <div className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded p-2" data-testid="p3-legacy-editable-note">
          Legacy quote — the first override you save will persist a normalized frozen snapshot.
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="p3-price-table">
          <thead className="bg-neutral-50 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">Sticker</th>
              <th className="text-left px-3 py-2">Subject</th>
              <th className="text-left px-3 py-2">Variant</th>
              <th className="text-right px-3 py-2">Qty</th>
              <th className="text-right px-3 py-2">Original</th>
              <th className="text-right px-3 py-2">Current</th>
              <th className="text-right px-3 py-2">Line total</th>
              <th className="text-left px-3 py-2 w-40">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(bd.lines || []).map((ln) => (
              <PriceRow
                key={ln.code}
                line={ln}
                subject={findSubject(quote, ln.code)}
                variant={ln.variant || quoteVariant}
                editable={editable}
                publicId={quote.public_id}
                snapshotUpdatedAt={snapshotUpdatedAt}
                onSaved={onReload}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 grid grid-cols-[1fr_auto] gap-y-1 max-w-md ml-auto text-sm" data-testid="p3-totals">
        <div className="text-[var(--smoke)]">Subtotal</div>
        <div className="font-mono text-right" data-testid="p3-subtotal">${((bd.subtotal_cents ?? 0)/100).toFixed(2)}</div>
        {bd.tax_rate_source !== "legacy" && (
          <>
            <div className="text-[var(--smoke)]">Tax {bd.tax_rate ? `(${(bd.tax_rate*100).toFixed(2)}%)` : ""}</div>
            <div className="font-mono text-right" data-testid="p3-tax">${((bd.tax_cents ?? 0)/100).toFixed(2)}</div>
          </>
        )}
        {bd.shipping_cents ? (
          <>
            <div className="text-[var(--smoke)]">Shipping</div>
            <div className="font-mono text-right" data-testid="p3-shipping">${((bd.shipping_cents ?? 0)/100).toFixed(2)}</div>
          </>
        ) : null}
        <div className="font-bold">Grand total</div>
        <div className="font-mono text-right font-bold" data-testid="p3-total">${((bd.total_cents ?? 0)/100).toFixed(2)}</div>
      </div>

      {editable && (
        <AddLinePanel
          publicId={quote.public_id}
          variant={quoteVariant}
          snapshotUpdatedAt={snapshotUpdatedAt}
          onAdded={onReload}
        />
      )}

      {/* P3.2 · Send Updated Quote — admin-controlled one-shot email */}
      <SendUpdateQuoteAction quote={quote} onSent={onReload} />
    </Section>
  );
}

// P3.2 · Canonical variant vocabulary + customer-friendly labels.
export const VARIANT_LABELS = {
  white:        "White",
  blue:         "Blue Parallel",
  gold_crumple: "Gold Crumple",
  red:          "Red Parallel",
  orange:       "Orange Parallel",
  purple:       "Purple Parallel",
};
export const VARIANT_TESTID = (v) => `variant-badge-${v || "white"}`;


// ── P1 · Read-only Quote Items table for legacy quotes ────────────
// Renders whatever verbatim line data the frozen `quote_snapshot`
// carries. Never touches master pricing — this is a preservation
// view, NOT an editable breakdown.
function QuoteItemsReadOnly({ quote, variant }) {
  const lines = (
    quote?.quote_snapshot?.lines
    || quote?.pricing_snapshot?.lines
    || []
  );
  if (!Array.isArray(lines) || lines.length === 0) {
    return (
      <div
        data-testid="quote-items-empty"
        className="text-sm text-[var(--smoke)]"
      >
        No priced line data on this quote.
      </div>
    );
  }
  const money = (cents) =>
    typeof cents === "number" ? `$${(cents / 100).toFixed(2)}` : "—";
  return (
    <div className="overflow-x-auto" data-testid="quote-items-readonly">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-[var(--smoke)]">
          <tr>
            <th className="py-2 pr-2">Code</th>
            <th className="py-2 pr-2">Subject</th>
            <th className="py-2 pr-2">Variant</th>
            <th className="py-2 pr-2 text-right">Qty</th>
            <th className="py-2 pr-2 text-right">Unit</th>
            <th className="py-2 pr-2 text-right">Line total</th>
            <th className="py-2 pr-2">Availability</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((ln, i) => {
            const unitCents = typeof ln.effective_unit_price_cents === "number"
              ? ln.effective_unit_price_cents
              : (typeof ln.unit_price === "number"
                  ? Math.round(ln.unit_price * 100) : null);
            const totalCents = typeof ln.line_total_cents === "number"
              ? ln.line_total_cents
              : (typeof ln.line_total === "number"
                  ? Math.round(ln.line_total * 100) : null);
            const v = ln.variant || variant || "white";
            return (
              <tr
                key={`${ln.code}-${i}`}
                data-testid={`quote-item-row-${ln.code}`}
                className="border-t border-[color:var(--gm-mist)]/30"
              >
                <td className="py-2 pr-2 font-mono">{ln.code}</td>
                <td className="py-2 pr-2">
                  {ln.subject || ln.display_name || "—"}
                </td>
                <td className="py-2 pr-2">
                  <QuoteVariantBadge variant={v} placement="row" />
                </td>
                <td className="py-2 pr-2 text-right">{ln.qty ?? "—"}</td>
                <td className="py-2 pr-2 text-right font-mono">
                  {money(unitCents)}
                </td>
                <td className="py-2 pr-2 text-right font-mono">
                  {money(totalCents)}
                </td>
                <td className="py-2 pr-2 text-xs">
                  {ln.availability_status || ln.availability || "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


// ── P1 · Legacy Rebuild Panel ──────────────────────────────────
// Two-step Preview → Save flow. The Preview call is read-only; only
// after the admin reviews the numbers and clicks Save do we mutate
// the DB. Owner directive 2026-02-19 (revised 2026-02-20 per
// verification audit): rebuild MUST reconstruct historical prices
// from the frozen quote only — never from today's master pricing —
// and must NOT overwrite `quote_snapshot.totals`.
function LegacyRebuildPanel({ quote, onReload }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const doPreview = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.post(
        `/admin/quotes/${encodeURIComponent(quote.public_id)}/pricing-snapshot/preview-rebuild`,
      );
      setPreview(r.data || null);
      if (r.data?.status === "ok") {
        toast("Historical reconstruction ready — review then click Save.");
      } else {
        toast.error("Reconstruction blocked — see details below.");
      }
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || "Preview failed.";
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const doSave = async () => {
    if (!window.confirm(
      "Save this historically-reconstructed breakdown? The frozen "
      + "customer-facing totals on this quote will remain unchanged; "
      + "only the internal pricing_snapshot will be added so admins "
      + "can now edit lines."
    )) return;
    setSaving(true); setError(null);
    try {
      await api.post(
        `/admin/quotes/${encodeURIComponent(quote.public_id)}/pricing-snapshot/persist`,
      );
      toast.success("Editable breakdown saved.");
      setPreview(null);
      onReload && onReload();
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || "Save failed.";
      setError(msg);
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const isBlocked = preview && preview.status !== "ok";
  const canSave = preview && preview.status === "ok" && !!preview.preview;

  return (
    <div
      className="mt-4 rounded-md border border-[color:var(--gm-mist)]/40 bg-[color:var(--gm-mist)]/5 p-3 text-sm"
      data-testid="p3-legacy-unavailable"
    >
      <div className="font-medium mb-1">
        Detailed editable pricing is not available for this legacy quote.
      </div>
      <div className="text-[var(--smoke)]">
        The items above are the frozen historical snapshot. Preview a
        rebuild that reconstructs the pricing from the historical
        evidence stored on this quote (never from today&apos;s master
        pricing). Nothing is written until you click Save, and the
        customer-facing frozen totals will stay untouched.
      </div>
      <div className="mt-3 flex gap-2 flex-wrap">
        <button
          type="button"
          data-testid="p3-legacy-rebuild-preview-btn"
          onClick={doPreview}
          disabled={loading}
          className="rounded-md bg-[var(--panini-red)] px-3 py-1.5 text-xs font-black uppercase tracking-widest text-white border-2 border-[var(--ink)] hover:brightness-110 disabled:opacity-50"
        >
          {loading ? "Building preview…" : (preview ? "Refresh preview" : "Preview historical rebuild")}
        </button>
        {canSave && (
          <button
            type="button"
            data-testid="p3-legacy-rebuild-save-btn"
            onClick={doSave}
            disabled={saving}
            className="rounded-md bg-emerald-700 px-3 py-1.5 text-xs font-black uppercase tracking-widest text-white border-2 border-[var(--ink)] hover:brightness-110 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save rebuilt breakdown"}
          </button>
        )}
      </div>
      {error && (
        <div className="mt-2 text-xs text-red-800" data-testid="p3-legacy-rebuild-error">
          {error}
        </div>
      )}
      {isBlocked && (
        <RebuildBlockedPanel preview={preview} />
      )}
      {canSave && (
        <div className="mt-4" data-testid="p3-legacy-rebuild-preview">
          <div className="text-[10px] uppercase tracking-widest font-bold mb-2 text-emerald-900">
            Preview — not yet saved · reconciles with historical totals
          </div>
          <RebuildPreviewTable preview={preview.preview} provenance={preview.provenance_summary} />
          <ReconcileTable historical={preview.historical_totals} reconstructed={preview.reconstructed_totals} />
        </div>
      )}
    </div>
  );
}


function RebuildBlockedPanel({ preview }) {
  const money = (c) =>
    typeof c === "number" ? `$${(c / 100).toFixed(2)}` : "—";
  const reason = preview?.reason || "unknown";
  const labels = {
    unreconstructable_lines:    "Some historical lines lack a determinate historical price. Persist is blocked to avoid inventing values.",
    no_historical_lines:        "This quote has no historical line-level pricing evidence. It cannot be rebuilt from history and must be recreated manually if needed.",
    historical_totals_missing:  "The historical totals block is incomplete. Reconciliation is impossible.",
    totals_did_not_reconcile:   "The reconstructed totals do not match the historical stored totals within one cent of rounding drift. Persist is blocked to preserve historical financial integrity.",
  };
  return (
    <div className="mt-4 border border-red-500/60 bg-red-50 p-3 rounded-md"
         data-testid="p3-legacy-rebuild-blocked">
      <div className="text-[10px] uppercase tracking-widest font-bold text-red-800 mb-2">
        Reconstruction blocked · {reason}
      </div>
      <div className="text-xs text-red-900 mb-3">{labels[reason] || reason}</div>
      {(preview.unreconstructable_lines || []).length > 0 && (
        <div className="mt-2" data-testid="p3-legacy-rebuild-unreconstructable">
          <div className="text-[10px] uppercase tracking-widest font-bold text-red-800 mb-1">
            Unreconstructable lines ({preview.unreconstructable_lines.length})
          </div>
          <ul className="text-xs font-mono text-red-900 list-disc pl-5">
            {preview.unreconstructable_lines.map((u, i) => (
              <li key={i}>
                {u.code || "?"} — {u.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      {preview.reconstructed_totals && preview.historical_totals && (
        <div className="mt-3 text-xs">
          <div className="text-[10px] uppercase tracking-widest font-bold text-red-800 mb-1">
            Historical vs reconstructed
          </div>
          <div className="grid grid-cols-3 gap-2 text-red-900">
            <div className="font-bold">Metric</div>
            <div className="font-bold text-right">Historical</div>
            <div className="font-bold text-right">Reconstructed</div>
            <div>Subtotal</div>
            <div className="text-right font-mono">{money(preview.historical_totals.subtotal_cents)}</div>
            <div className="text-right font-mono">{money(preview.reconstructed_totals.subtotal_cents)}</div>
            <div>Tax</div>
            <div className="text-right font-mono">{money(preview.historical_totals.tax_cents)}</div>
            <div className="text-right font-mono">{money(preview.reconstructed_totals.tax_cents)}</div>
            <div>Total</div>
            <div className="text-right font-mono">{money(preview.historical_totals.total_cents)}</div>
            <div className="text-right font-mono">{money(preview.reconstructed_totals.total_cents)}</div>
          </div>
        </div>
      )}
    </div>
  );
}


function ReconcileTable({ historical, reconstructed }) {
  if (!historical || !reconstructed) return null;
  const money = (c) =>
    typeof c === "number" ? `$${(c / 100).toFixed(2)}` : "—";
  const row = (label, h, r) => (
    <>
      <div className="text-[var(--smoke)]">{label}</div>
      <div className="font-mono text-right">{money(h)}</div>
      <div className="font-mono text-right">{money(r)}</div>
    </>
  );
  return (
    <div className="mt-3 grid grid-cols-[1fr_auto_auto] gap-x-4 gap-y-1 max-w-md ml-auto text-sm"
         data-testid="p3-reconcile-table">
      <div className="text-[10px] uppercase tracking-widest font-bold">Metric</div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-right">Historical</div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-right">Reconstructed</div>
      {row("Subtotal", historical.subtotal_cents, reconstructed.subtotal_cents)}
      {row("Tax", historical.tax_cents, reconstructed.tax_cents)}
      {row("Total", historical.total_cents, reconstructed.total_cents)}
    </div>
  );
}


function RebuildPreviewTable({ preview, provenance }) {
  const lines = preview?.lines || [];
  const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border border-neutral-300 rounded">
        <thead className="bg-neutral-50 text-[10px] uppercase tracking-widest">
          <tr>
            <th className="text-left px-3 py-2">Code</th>
            <th className="text-left px-3 py-2">Variant</th>
            <th className="text-right px-3 py-2">Qty</th>
            <th className="text-right px-3 py-2">Unit</th>
            <th className="text-right px-3 py-2">Line total</th>
            <th className="text-left px-3 py-2">Source</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((ln) => (
            <tr key={ln.code} className="border-t border-neutral-200" data-testid={`p3-preview-row-${ln.code}`}>
              <td className="px-3 py-2 font-mono font-bold">{ln.code}</td>
              <td className="px-3 py-2"><LineVariantChip variant={ln.variant} /></td>
              <td className="px-3 py-2 text-right font-mono">{ln.qty}</td>
              <td className="px-3 py-2 text-right font-mono">{money(ln.effective_unit_price_cents)}</td>
              <td className="px-3 py-2 text-right font-mono">{money(ln.line_total_cents)}</td>
              <td className="px-3 py-2 text-[10px] text-[var(--smoke)]" data-testid={`p3-preview-source-${ln.code}`}>
                {ln.historical_price_source || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {provenance && Object.keys(provenance).length > 0 && (
        <div className="mt-2 text-[10px] text-[var(--smoke)]" data-testid="p3-preview-provenance">
          Historical price provenance:{" "}
          {Object.entries(provenance).map(([k, v]) => `${k}×${v}`).join(", ")}
        </div>
      )}
    </div>
  );
}


// ── P1 · Add Line panel (editable modern quotes only) ─────────
// Wires to `POST /admin/quotes/{pid}/lines`. Owner supplies the
// unit price in dollars — the backend expects cents so we convert
// here. Rejects sub-cent precision the same way `PriceRow` does.
function AddLinePanel({ publicId, variant, snapshotUpdatedAt, onAdded }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [subject, setSubject] = useState("");
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setCode(""); setSubject(""); setQty("1");
    setPrice(""); setReason(""); setError(null);
  };

  const submit = async () => {
    setError(null);
    const trimmedCode = (code || "").trim().toUpperCase();
    const qtyNum = parseInt(qty, 10);
    const priceNum = parseFloat(String(price).replace(",", "."));
    if (!trimmedCode) { setError("Sticker code is required."); return; }
    if (!(qtyNum >= 1)) { setError("Qty must be at least 1."); return; }
    if (!(priceNum >= 0) || Number.isNaN(priceNum)) {
      setError("Unit price must be a non-negative amount.");
      return;
    }
    const cents = Math.round(priceNum * 100);
    if (Math.abs(cents / 100 - priceNum) > 0.0001) {
      setError("Only cent precision is supported.");
      return;
    }
    setBusy(true);
    try {
      await api.post(
        `/admin/quotes/${encodeURIComponent(publicId)}/lines`,
        {
          code: trimmedCode,
          qty: qtyNum,
          unit_price_cents: cents,
          subject: subject.trim() || null,
          variant: variant || null,
          reason: reason.trim() || null,
          snapshot_updated_at: snapshotUpdatedAt,
        },
      );
      toast.success(`Added ${trimmedCode}.`);
      reset();
      setOpen(false);
      onAdded && onAdded();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "snapshot_stale") {
        toast.error("This quote's pricing changed since you opened it. Refreshing.");
        onAdded && onAdded();
        setOpen(false);
      } else {
        setError(formatApiError(d) || "Add failed.");
      }
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <div className="mt-5 pt-4 border-t border-neutral-200">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] bg-white hover:bg-[var(--ink)] hover:text-white font-bold uppercase tracking-wider"
          data-testid="p3-add-line-open"
        >
          + Add sticker
        </button>
      </div>
    );
  }

  return (
    <div
      className="mt-5 pt-4 border-t border-neutral-200"
      data-testid="p3-add-line-panel"
    >
      <div className="text-[10px] uppercase tracking-widest font-bold mb-2">
        Add a sticker
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_100px_120px] gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Code (e.g. FWC1)"
          className="border-2 border-[var(--ink)] rounded-lg px-2 py-1.5 text-sm font-mono uppercase"
          data-testid="p3-add-line-code"
          disabled={busy}
        />
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject / Player (optional)"
          className="border-2 border-[var(--ink)] rounded-lg px-2 py-1.5 text-sm"
          data-testid="p3-add-line-subject"
          disabled={busy}
        />
        <input
          type="number"
          min="1"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          placeholder="Qty"
          className="border-2 border-[var(--ink)] rounded-lg px-2 py-1.5 text-sm text-right"
          data-testid="p3-add-line-qty"
          disabled={busy}
        />
        <input
          type="text"
          inputMode="decimal"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="$ Unit price"
          className="border-2 border-[var(--ink)] rounded-lg px-2 py-1.5 text-sm text-right"
          data-testid="p3-add-line-price"
          disabled={busy}
        />
      </div>
      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={200}
        placeholder="Reason (optional)"
        className="mt-2 w-full border border-neutral-300 rounded-lg px-2 py-1.5 text-sm"
        data-testid="p3-add-line-reason"
        disabled={busy}
      />
      {error && (
        <div className="mt-2 text-xs text-red-800" data-testid="p3-add-line-error">
          {error}
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-yellow)] hover:brightness-110 font-bold uppercase tracking-wider disabled:opacity-50"
          data-testid="p3-add-line-submit"
        >
          {busy ? "Adding…" : "Add sticker"}
        </button>
        <button
          type="button"
          onClick={() => { reset(); setOpen(false); }}
          disabled={busy}
          className="text-xs px-3 py-1.5 rounded-full border border-neutral-400 bg-white hover:bg-neutral-100 uppercase tracking-wider disabled:opacity-50"
          data-testid="p3-add-line-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}


function QuoteVariantBadge({ variant, placement }) {
  const v = variant || "white";
  const label = VARIANT_LABELS[v] || VARIANT_LABELS.white;
  if (v === "white") {
    return (
      <div className="mb-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold"
           data-testid={VARIANT_TESTID(v)}>
        <span className="inline-block w-2 h-2 rounded-full bg-neutral-300" />
        Variant · {label}
      </div>
    );
  }
  const tone = {
    blue:         "bg-blue-600 text-white",
    gold_crumple: "bg-amber-500 text-black",
    red:          "bg-red-600 text-white",
    orange:       "bg-orange-500 text-white",
    purple:       "bg-purple-600 text-white",
  }[v] || "bg-neutral-800 text-white";
  return (
    <div className={"mb-3 inline-block px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest " + tone}
         data-testid={VARIANT_TESTID(v)}>
      {label}
    </div>
  );
}

function LineVariantChip({ variant }) {
  const v = variant || "white";
  const label = VARIANT_LABELS[v] || VARIANT_LABELS.white;
  const tone = {
    white:        "bg-neutral-100 text-neutral-600 border-neutral-300",
    blue:         "bg-blue-50 text-blue-800 border-blue-300",
    gold_crumple: "bg-amber-50 text-amber-800 border-amber-300",
    red:          "bg-red-50 text-red-800 border-red-300",
    orange:       "bg-orange-50 text-orange-800 border-orange-300",
    purple:       "bg-purple-50 text-purple-800 border-purple-300",
  }[v];
  return (
    <span className={"inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest border " + tone}
          data-testid={`line-variant-${v}`}>
      {label}
    </span>
  );
}

function SendUpdateQuoteAction({ quote, onSent }) {
  const status = quote?.update_email_status;
  const [busy, setBusy] = useState(false);
  if (!status) return null;
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/quotes/${encodeURIComponent(quote.public_id)}/send-update-email`);
      toast.success(`Updated quote email sent to ${r.data?.recipient || "customer"}.`);
      onSent && onSent();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "unchanged_since_last_send") {
        toast.error("Nothing to update — pricing hasn't changed since the last send.");
      } else if (d && typeof d === "object" && d.code === "email_provider_not_configured") {
        toast.error("Email provider isn't configured. Configure Resend before sending updates.");
      } else {
        toast.error(formatApiError(d) || "Send failed.");
      }
      onSent && onSent();
    } finally { setBusy(false); }
  };
  const reasonLabel = {
    quote_locked:                 "Quote is finalized — cannot send updates.",
    no_customer_email:            "No customer email on file.",
    no_frozen_snapshot:           "Quote has no frozen pricing snapshot.",
    unchanged_since_last_send:    "Pricing hasn't changed since the last update email.",
  }[status.reason];
  return (
    <div className="mt-5 pt-4 border-t border-neutral-200 flex flex-wrap items-center justify-between gap-3"
         data-testid="p3-send-update-block">
      <div className="text-xs">
        {status.eligible ? (
          <span className="text-emerald-700 font-bold" data-testid="p3-send-update-eligible">
            {status.last_sent_at
              ? "Effective pricing has changed since the last email was sent."
              : "Ready to notify the customer of the current effective pricing."}
          </span>
        ) : (
          <span className="text-[var(--smoke)] italic" data-testid="p3-send-update-blocked">{reasonLabel}</span>
        )}
        {status.last_sent_at && (
          <div className="text-[10px] text-[var(--smoke)]" data-testid="p3-send-update-last-sent">
            Last sent: {new Date(status.last_sent_at).toLocaleString()}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={send}
        disabled={!status.eligible || busy}
        className={"px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest border-2 " +
          (status.eligible && !busy
            ? "bg-[var(--panini-red)] text-white border-[var(--ink)] hover:brightness-110"
            : "bg-neutral-100 text-neutral-400 border-neutral-300 cursor-not-allowed")}
        data-testid="p3-send-update-btn"
      >
        {busy ? "Sending…" : "Send Updated Quote"}
      </button>
    </div>
  );
}

function findSubject(quote, code) {
  const bag =
    (quote?.parsed_snapshot?.items || []).find((x) => x.code === code) ||
    (quote?.quote_snapshot?.lines || []).find((x) => x.code === code) ||
    {};
  return bag.display_name || bag.player_name || bag.subject || "";
}

function PriceRow({ line, subject, variant, editable, publicId, snapshotUpdatedAt, onSaved }) {
  const origDollars = ((line.original_unit_price_cents ?? 0) / 100).toFixed(2);
  const effDollars = ((line.effective_unit_price_cents ?? 0) / 100).toFixed(2);
  const lineTotal = ((line.line_total_cents ?? 0) / 100).toFixed(2);
  const overridden = !!line.override || line.effective_unit_price_cents !== line.original_unit_price_cents;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(effDollars);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [inlineError, setInlineError] = useState(null);

  const save = async () => {
    const val = parseFloat(String(draft).replace(",", "."));
    if (!(val >= 0) || Number.isNaN(val)) {
      setInlineError("Enter a non-negative amount"); return;
    }
    // Reject sub-cent precision.
    const cents = Math.round(val * 100);
    if (Math.abs(cents / 100 - val) > 0.0001) {
      setInlineError("Only cent precision is supported"); return;
    }
    setBusy(true); setInlineError(null);
    try {
      await api.patch(
        `/admin/quotes/${encodeURIComponent(publicId)}/lines/${encodeURIComponent(line.code)}/price`,
        {
          new_unit_price_cents: cents,
          reason: reason || null,
          snapshot_updated_at: snapshotUpdatedAt,
        },
      );
      toast.success("Price saved.");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "snapshot_stale") {
        toast.error("This quote's pricing changed since you opened it. Refreshing.");
        onSaved && onSaved();
      } else {
        setInlineError(formatApiError(d) || "Save failed.");
      }
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.post(
        `/admin/quotes/${encodeURIComponent(publicId)}/lines/${encodeURIComponent(line.code)}/reset`,
        { snapshot_updated_at: snapshotUpdatedAt },
      );
      toast.success("Reset to original.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Reset failed.");
    } finally { setBusy(false); }
  };

  const removeLine = async () => {
    if (!window.confirm(
      `Remove sticker ${line.code} from this quote? Totals will be recalculated.`
    )) return;
    setBusy(true);
    try {
      await api.delete(
        `/admin/quotes/${encodeURIComponent(publicId)}/lines/${encodeURIComponent(line.code)}`,
        { data: { snapshot_updated_at: snapshotUpdatedAt } },
      );
      toast.success(`Removed ${line.code}.`);
      onSaved && onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "snapshot_stale") {
        toast.error("This quote's pricing changed since you opened it. Refreshing.");
        onSaved && onSaved();
      } else {
        toast.error(formatApiError(d) || "Remove failed.");
      }
    } finally { setBusy(false); }
  };

  return (
    <tr className="border-t border-neutral-200" data-testid={`p3-row-${line.code}`}>
      <td className="px-3 py-2 font-mono font-bold">{line.code}</td>
      <td className="px-3 py-2 text-[var(--smoke)]">{subject || "—"}</td>
      <td className="px-3 py-2"><LineVariantChip variant={variant || line.variant} /></td>
      <td className="px-3 py-2 text-right font-mono">{line.qty}</td>
      <td className="px-3 py-2 text-right font-mono" data-testid={`p3-original-${line.code}`}>${origDollars}</td>
      <td className="px-3 py-2 text-right font-mono">
        {editable && editing ? (
          <div className="flex items-center gap-1 justify-end">
            <span>$</span>
            <input
              type="text"
              inputMode="decimal"
              className="w-20 border rounded px-1.5 py-0.5 text-xs text-right"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={busy}
              data-testid={`p3-input-${line.code}`}
            />
          </div>
        ) : (
          <span data-testid={`p3-current-${line.code}`}>${effDollars}</span>
        )}
        {overridden && !editing && (
          <div className="mt-0.5 inline-block text-[9px] font-bold uppercase tracking-widest text-red-700" data-testid={`p3-overridden-${line.code}`}>OVERRIDDEN</div>
        )}
      </td>
      <td className="px-3 py-2 text-right font-mono" data-testid={`p3-line-total-${line.code}`}>${lineTotal}</td>
      <td className="px-3 py-2">
        {!editable ? (
          <span className="text-[10px] text-[var(--smoke)]">Locked</span>
        ) : editing ? (
          <div className="flex flex-col gap-1">
            <input
              type="text"
              className="w-full border rounded px-1.5 py-0.5 text-xs"
              placeholder="Reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={200}
              disabled={busy}
              data-testid={`p3-reason-${line.code}`}
            />
            <div className="flex gap-1">
              <button type="button" onClick={save} disabled={busy} className="btn-yellow text-xs" data-testid={`p3-save-${line.code}`}>
                {busy ? "Saving…" : "Save"}
              </button>
              <button type="button" onClick={() => { setEditing(false); setInlineError(null); setDraft(effDollars); setReason(""); }} disabled={busy} className="btn-ghost text-xs" data-testid={`p3-cancel-${line.code}`}>
                Cancel
              </button>
            </div>
            {inlineError && (
              <div className="text-[10px] text-red-700" data-testid={`p3-error-${line.code}`}>{inlineError}</div>
            )}
          </div>
        ) : (
          <div className="flex gap-1 flex-wrap">
            <button type="button" onClick={() => setEditing(true)} className="btn-ghost text-xs" data-testid={`p3-edit-${line.code}`}>
              Edit
            </button>
            {overridden && (
              <button type="button" onClick={reset} disabled={busy} className="btn-ghost text-xs" data-testid={`p3-reset-${line.code}`}>
                Reset
              </button>
            )}
            <button
              type="button"
              onClick={removeLine}
              disabled={busy}
              className="text-xs px-2 py-0.5 rounded border border-red-700 text-red-700 hover:bg-red-700 hover:text-white font-bold uppercase tracking-wider disabled:opacity-50"
              data-testid={`p3-remove-${line.code}`}
            >
              Remove
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}


function StatBox({ label, value }) {
  return (
    <div className="border border-neutral-300 rounded-lg p-3 bg-neutral-50 text-center">
      <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold">{label}</div>
      <div className="mt-1 font-display text-xl">{value}</div>
    </div>
  );
}



/**
 * Q5.3 · Compact admin-only sales action strip.
 *
 * - Internal admin note editor (2000-char cap enforced server-side).
 * - "Mark contacted" button — stamps `admin_contacted_at` in UTC.
 * - "Suppress recovery" — flips the canonical suppression field.
 * - Contact deep-links: WhatsApp / email / phone, sourced from the
 *   existing customer.* fields (no new storage).
 */
function SalesActions({ q, onChanged }) {
  const [note, setNote] = useState(q.admin_note || "");
  const [savingNote, setSavingNote] = useState(false);
  const [markingContacted, setMarkingContacted] = useState(false);
  const [suppressing, setSuppressing] = useState(false);

  // Refresh the local editor state whenever the parent reloads the
  // quote (e.g. after "Mark contacted" bumps `updated_at`).
  useEffect(() => { setNote(q.admin_note || ""); }, [q.admin_note]);

  const noteDirty = (note || "").trim() !== (q.admin_note || "").trim();
  const suppressed = Boolean(q.quote_recovery_suppressed);
  const reason = q.quote_recovery_suppressed_reason;
  const phoneDigits = String(q.customer?.phone || "").replace(/\D/g, "");
  const email = q.customer?.email || "";

  const saveNote = async () => {
    if (note.length > 2000) {
      toast.error("Note is too long (max 2000 characters).");
      return;
    }
    setSavingNote(true);
    try {
      await api.patch(
        `/admin/quotes/${encodeURIComponent(q.public_id)}/note`,
        { admin_note: note.trim() || null },
      );
      toast.success("Note saved");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingNote(false);
    }
  };

  const markContacted = async () => {
    setMarkingContacted(true);
    try {
      await api.post(
        `/admin/quotes/${encodeURIComponent(q.public_id)}/mark-contacted`,
      );
      toast.success("Marked contacted");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setMarkingContacted(false);
    }
  };

  const suppress = async () => {
    if (!window.confirm(
      "Suppress recovery for this quote? No further Q3 recovery emails will send. Prior sends and timestamps are untouched.",
    )) return;
    setSuppressing(true);
    try {
      await api.post(
        `/admin/quotes/${encodeURIComponent(q.public_id)}/suppress`,
        { reason: "manual_admin" },
      );
      toast.success("Recovery suppressed");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSuppressing(false);
    }
  };

  return (
    <div
      className="border-2 border-[var(--ink)] rounded-2xl bg-[var(--panini-yellow-soft)] p-5 mb-5"
      data-testid="quote-sales-actions"
    >
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink)] mb-3">
        Sales actions
      </div>

      {/* Contact deep-links + suppression state */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        {phoneDigits && (
          <a
            href={`https://wa.me/${phoneDigits}`}
            target="_blank" rel="noopener noreferrer"
            data-testid="quote-contact-whatsapp"
            className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] bg-white hover:bg-[#25D366] hover:text-white font-bold uppercase tracking-wider"
          >
            WhatsApp
          </a>
        )}
        {email && (
          <a
            href={`mailto:${encodeURIComponent(email)}`}
            data-testid="quote-contact-email"
            className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] bg-white hover:bg-[var(--ink)] hover:text-white font-bold uppercase tracking-wider"
          >
            Email
          </a>
        )}
        {phoneDigits && (
          <a
            href={`tel:${phoneDigits}`}
            data-testid="quote-contact-phone"
            className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] bg-white hover:bg-[var(--ink)] hover:text-white font-bold uppercase tracking-wider"
          >
            Call
          </a>
        )}
        {q.admin_contacted_at && (
          <span
            className="text-[11px] px-3 py-1.5 rounded-full bg-emerald-100 border border-emerald-800 text-emerald-900"
            data-testid="quote-contacted-badge"
          >
            Last contacted {detailLong(q.admin_contacted_at)}
          </span>
        )}
        {suppressed && (
          <span
            className="text-[11px] px-3 py-1.5 rounded-full bg-neutral-200 border border-neutral-600 text-neutral-800 uppercase tracking-wider font-bold"
            data-testid="quote-suppressed-badge"
            title={reason ? `reason: ${reason}` : ""}
          >
            Recovery suppressed{reason ? ` · ${reason}` : ""}
          </span>
        )}
      </div>

      {/* Buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          onClick={markContacted}
          disabled={markingContacted}
          data-testid="quote-mark-contacted"
          className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] bg-white hover:bg-[var(--ink)] hover:text-white font-bold uppercase tracking-wider disabled:opacity-50"
        >
          {markingContacted ? "Marking…" : "Mark contacted"}
        </button>
        {!suppressed && (
          <button
            type="button"
            onClick={suppress}
            disabled={suppressing}
            data-testid="quote-suppress-recovery"
            className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--panini-red)] text-[var(--panini-red)] bg-white hover:bg-[var(--panini-red)] hover:text-white font-bold uppercase tracking-wider disabled:opacity-50"
          >
            {suppressing ? "Suppressing…" : "Suppress recovery"}
          </button>
        )}
      </div>

      {/* Internal admin note */}
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink)]">
          Internal note
        </span>
        <textarea
          rows={3}
          value={note}
          maxLength={2000}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Only visible to admins. Max 2000 characters."
          data-testid="quote-admin-note-input"
          className="mt-1 w-full border-2 border-[var(--ink)] rounded-xl p-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
        />
        <div className="flex justify-between items-center mt-1 text-[11px] text-[var(--smoke)]">
          <span>{note.length} / 2000</span>
          <button
            type="button"
            onClick={saveNote}
            disabled={!noteDirty || savingNote}
            data-testid="quote-admin-note-save"
            className="text-xs px-3 py-1 rounded-full border-2 border-[var(--ink)] bg-white hover:bg-[var(--ink)] hover:text-white font-bold uppercase tracking-wider disabled:opacity-40"
          >
            {savingNote ? "Saving…" : "Save note"}
          </button>
        </div>
      </label>
    </div>
  );
}


/**
 * Q5.4 · Quote Journey timeline.
 * Compact stage strip: Created → Immediate → R1 → R2 → R3 → Order.
 * Reads only fields already returned by GET /api/admin/quotes/{id}.
 */
function QuoteJourney({ q }) {
  const suppressed = Boolean(q.quote_recovery_suppressed);
  const recoveryStage = Math.max(0, Number(q.recovery_stage || 0));
  const converted = Boolean(q.converted_order_public_id);
  const linkedOrderStatus = q.linked_order?.status || null;

  const raw = [
    { key: "created",   label: "Created",   at: q.created_at },
    { key: "immediate", label: "Immediate", at: q.immediate_email_sent_at, isRecovery: false },
    { key: "r1", label: "R1", at: q.recovery_1_sent_at, isRecovery: true },
    { key: "r2", label: "R2", at: q.recovery_2_sent_at, isRecovery: true },
    { key: "r3", label: "R3", at: q.recovery_3_sent_at, isRecovery: true },
    { key: "order", label: "Order", at: q.linked_order?.created_at || (converted ? q.updated_at : null), isOrder: true },
  ];

  const stages = raw.map((s) => {
    if (s.key === "order") {
      if (converted) return { ...s, state: "completed" };
      if (suppressed && recoveryStage < 3) return { ...s, state: "suppressed" };
      return { ...s, state: "pending" };
    }
    if (s.at) return { ...s, state: "completed" };
    if (s.isRecovery && suppressed) return { ...s, state: "suppressed" };
    return { ...s, state: "pending" };
  });

  const fmt = (ts) => { if (!ts) return ""; try { return detailLong(ts); } catch (_e) { return String(ts); } };

  return (
    <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5 mb-5" data-testid="quote-journey">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink)] mb-3">
        Quote Journey
      </div>
      <div className="flex flex-col md:flex-row md:items-stretch md:flex-wrap gap-2 md:gap-0">
        {stages.map((s, i) => (
          <div
            key={s.key}
            data-testid={`journey-stage-${s.key}`}
            data-state={s.state}
            className={`flex-1 min-w-[8rem] flex md:items-center md:flex-row items-start ${i > 0 ? "md:pl-3" : ""}`}
          >
            {i > 0 && (
              <div aria-hidden className="hidden md:block h-[2px] w-3 mr-2 bg-[var(--ink)]/25" />
            )}
            <div className={`w-full rounded-xl border-2 px-3 py-2 text-xs ${
              s.state === "completed"
                ? "border-emerald-800 bg-emerald-50 text-emerald-900"
                : s.state === "suppressed"
                ? "border-neutral-500 bg-neutral-100 text-neutral-600 line-through opacity-70"
                : "border-[var(--line)] bg-white text-[var(--smoke)]"
            }`}>
              <div className="font-bold uppercase tracking-wider text-[10px]">
                {s.label}
                {s.isOrder && converted && linkedOrderStatus && (
                  <span className="ml-1 opacity-80">· {linkedOrderStatus}</span>
                )}
              </div>
              <div className={`mt-0.5 truncate ${s.state === "completed" ? "font-semibold" : ""}`}
                    data-testid={`journey-stage-${s.key}-ts`}>
                {s.state === "completed" ? (fmt(s.at) || "done")
                  : s.state === "suppressed" ? "Suppressed" : "Pending"}
              </div>
            </div>
          </div>
        ))}
      </div>
      {(q.admin_contacted_at || q.quote_recovery_suppressed || q.feedback?.submitted_at) && (
        <div className="flex flex-wrap gap-3 mt-4 text-[11px] text-[var(--smoke)]" data-testid="journey-secondary">
          {q.admin_contacted_at && (
            <span data-testid="journey-contacted">
              <b className="text-[var(--ink)]">Contacted:</b> {fmt(q.admin_contacted_at)}
            </span>
          )}
          {q.feedback?.submitted_at && (
            <span data-testid="journey-feedback">
              <b className="text-[var(--ink)]">Feedback:</b> {q.feedback?.reason || "—"} · {fmt(q.feedback.submitted_at)}
            </span>
          )}
          {suppressed && (
            <span data-testid="journey-suppressed">
              <b className="text-[var(--ink)]">Recovery suppressed:</b> {q.quote_recovery_suppressed_reason || "—"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

